/**
 * Prefix-aware navigation for the per-tab `/u/<n>` multi-account scheme.
 *
 * Every in-app `<Link to="/inbox">` / `navigate({ to: "/inbox" })` must stay
 * inside the CURRENT tab's `/u/<n>` prefix, otherwise navigating would drop the
 * active account (and TanStack wouldn't even match, since the app routes now
 * live at `/u/$u/inbox`, …). Rather than touch ~80 call sites individually, the
 * app imports `Link` / `useNavigate` from HERE instead of `@tanstack/solid-router`.
 *
 * The wrappers rewrite an absolute in-app `to` (`/inbox`) into the prefixed form
 * (`/u/<n>/inbox`) by reading the live index from THIS tab's URL. Auth / callback
 * paths (`/login`, …) and already-prefixed or relative `to`s are passed through
 * untouched. This is the single chokepoint to replicate to the 13 clones.
 *
 * Typing note: these are deliberately LOOSELY typed (`to?: string`, options as a
 * permissive record). The whole point is to keep passing the existing absolute
 * string paths (`/inbox`) that the strict typed router would reject now that the
 * real routes are `/u/$u/inbox`. Correctness is enforced at runtime by
 * `prefixTo`, not by the path union — the prefix is the safety net.
 */

import { Link as BaseLink, useNavigate as useBaseNavigate } from "@tanstack/solid-router";
import { type JSX, mergeProps, splitProps } from "solid-js";
import { buildUPath, readActiveIndexFromUrl } from "@/lib/active-account";
import { SOCIAL_CALLBACK_PATH } from "@/lib/social-callback-url";

const NON_PREFIXED_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  // Verificación pública de certificados (guest-first, fuera de /u/<n>).
  "/verify",
  // F3-D · página pública de profesor (guest-first, fuera de /u/<n>).
  "/teachers",
  // Fuente única (CC-24): mismo path real del callback OAuth que el router raíz.
  SOCIAL_CALLBACK_PATH,
];

export function isNonPrefixed(path: string): boolean {
  return NON_PREFIXED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Rewrites an absolute in-app `to` into the active tab's `/u/<n>` prefix.
 * Leaves untouched: non-string `to`, relative `to` (no leading `/`), already
 * prefixed `/u/...`, and auth/callback paths. Falls back to index 0 when the
 * URL has no prefix yet (e.g. navigating away from an auth page).
 */
function prefixTo<T>(to: T): T {
  if (typeof to !== "string" || !to.startsWith("/")) {
    return to;
  }
  if (to.startsWith("/u/") || isNonPrefixed(to)) {
    return to;
  }
  const index = readActiveIndexFromUrl() ?? 0;
  // `to` is a string here, so T is string; the double cast just re-narrows to T.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return buildUPath(index, to) as unknown as T;
}

/**
 * Loose navigate-function type for code that receives `navigate` as a prop and
 * passes absolute in-app string paths (`to: "/inbox"`). The strict typed router
 * would reject those now that real routes are `/u/$u/inbox`; this preserves the
 * ergonomic call sites while `prefixTo` enforces correctness at runtime.
 */
export type NavigateFn = (options?: {
  to?: string;
  params?: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>);
  search?: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>);
  replace?: boolean;
  hash?: string;
  state?: Record<string, unknown>;
}) => Promise<void> | void;

/**
 * `useNavigate` that auto-prefixes the `to` option with the current `/u/<n>`.
 * Drop-in for `@tanstack/solid-router`'s `useNavigate`: it returns the SAME
 * type, so it stays assignable everywhere a `ReturnType<typeof useNavigate>`
 * (the base one) is expected (e.g. hooks that receive `navigate` as a prop).
 * The `to`-rewrite happens at runtime regardless of the static type.
 */
export function useNavigate(): NavigateFn {
  const base = useBaseNavigate();
  // The base navigate is strictly typed against the route tree; we deliberately
  // widen it to accept the loose options we pass (bare paths + prefixed `to`).
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-type-assertion
  const loose = base as unknown as (o: unknown) => Promise<void>;
  return (options?: Parameters<NavigateFn>[0]) => {
    if (options && "to" in options) {
      return loose({ ...options, to: prefixTo(options.to) });
    }
    return loose(options);
  };
}

/**
 * `<Link>` that auto-prefixes its `to` with the current `/u/<n>`.
 * Drop-in for `@tanstack/solid-router`'s `Link`.
 */
type LinkProps = { to?: unknown } & Record<string, unknown>;

export function Link(props: LinkProps): JSX.Element {
  const [local, rest] = splitProps(props, ["to"]);
  // `BaseLink` accepts a far stricter prop type; we intentionally widen here.
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-type-assertion
  const renderLink = BaseLink as unknown as (p: Record<string, unknown>) => JSX.Element;
  // Expose `to` as a getter so the prefixing stays reactive: `BaseLink` reads it
  // inside its own tracked scope, so the active `/u/<n>` is re-resolved if the
  // tab navigates between accounts while the link is mounted.
  //
  // CC-39: forward `rest` with `mergeProps`, NEVER with an object spread.
  // `{ ...rest }` EVALUATES every prop getter once and freezes the results —
  // including `children` (a getter for dynamic JSX like `{t(msg`…`)}`), which
  // then stays stuck at its first-render value. On a cold prod boot that value
  // is the raw lingui hash id (catalog not loaded yet), so every Link label
  // rendered as a hash in BOTH locales. `mergeProps` keeps the getters lazy,
  // so BaseLink's own tracked reads (`createMemo(() => local.children)`)
  // re-derive when the catalog / any driving signal updates.
  const forwarded = mergeProps(rest, {
    get to() {
      return prefixTo(local.to);
    },
  });
  return renderLink(forwarded);
}
